import { useState, useEffect } from 'react';
import {
 Settings, Server, Save, TestTube, Plus, Trash2, RefreshCw, Phone,
 Users, Building2, Link2, Mail, MapPin, FileText, Key,
 ChevronDown, ChevronRight, Video, DoorOpen, UserPlus, Shield, Bell
, Radio, Activity, KeyRound, HelpCircle, BarChart3 } from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import adminSettingsTutorial from '../components/tutorials/configs/adminSettingsTutorial';
import { toast } from 'react-toastify';
import UserManagement from '../components/UserManagement';
import OAuthSettings from '../components/OAuthSettings';
import PendingRegistrations from '../components/PendingRegistrations';
import PushNotificationSettings from '../components/PushNotificationSettings';
import IntegrationsManager from "../components/IntegrationsManager";
import ControllersManager from "../components/ControllersManager";
import SplunkSettings from '../components/SplunkSettings';
import AuditLogTab from '../components/AuditLogTab';
import { useNavigate } from 'react-router-dom';
import useStore from '../stores/useStore';
import useAuthStore from '../stores/authStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import CcmLicenseCard from '../components/CcmLicenseCard';
import OrgLicenseCard from '../components/OrgLicenseCard';
import useCcmLicenseStore from '../stores/ccmLicenseStore';


// Map frontend platform names to backend VMS types
const platformToType = {
  wave: "WAVE_VMS",
  mirasys: "MIRASYS_VMS",
  milestone: "MILESTONE_VMS",
  genetec: "GENETEC_VMS"
};

const platformDefaults = {
  wave: { port: 7001, useHttps: true },
  mirasys: { port: 8082, useHttps: false }
};
const AdminSettings = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(adminSettingsTutorial);

 const { waveConfig, setWaveConfig } = useStore();
 const { currentTenant } = useAuthStore();
 
 // Tab state
 const [activeTab, setActiveTab] = useState('integrations');
 const navigate = useNavigate();
 
 // Collapsible sections state
 const [expandedSections, setExpandedSections] = useState({
 vms: false,
 accessControl: false,
 intercoms: false,
 pendingRegistrations: false,
 userManagement: false,
 oauth: false
 });
 
 // Config source tracking
 const [configSource, setConfigSource] = useState(null);
 
 // VMS Systems state (array for multiple systems)
 const [vmsSystems, setVmsSystems] = useState([]);
 const [cameras, setCameras] = useState([]);
 const [testingVms, setTestingVms] = useState({});
 
 // Access Control Systems state (array for multiple systems)
 const [accessControlSystems, setAccessControlSystems] = useState([]);
 const [testingAccessControl, setTestingAccessControl] = useState({});
 
 // Intercoms state
 const [intercoms, setIntercoms] = useState([]);
 
 // Save state
 const [saving, setSaving] = useState(false);
 
 // Company info state
 const [companyInfo, setCompanyInfo] = useState({
 name: '',
 address: {
 street: '',
 city: '',
 state: '',
 zip: '',
 country: 'United States'
 },
 contact: {
 phone: '',
 email: '',
 website: ''
 },
 industry: ''
 });
 const [savingCompany, setSavingCompany] = useState(false);

 // Phone number formatter - converts any format to (XXX) XXX-XXXX
 const formatPhoneNumber = (value) => {
 // Remove all non-digits
 const digits = value.replace(/\D/g, '');
 
 // Only format if we have 10 digits (US number)
 if (digits.length === 10) {
 return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
 }
 // If 11 digits starting with 1, strip the 1 and format
 if (digits.length === 11 && digits[0] === '1') {
 return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
 }
 // Return original if not a standard US number
 return value;
 };

 const handlePhoneBlur = (e) => {
 const formatted = formatPhoneNumber(e.target.value);
 setCompanyInfo({
 ...companyInfo,
 contact: { ...companyInfo.contact, phone: formatted }
 });
 };

 useEffect(() => {
 loadConfig();
 loadCompanyInfo();
 }, [currentTenant?.id]);

 // Auto-load cameras when VMS systems change
 useEffect(() => {
 const loadAllCameras = async () => {
 const connectedVms = vmsSystems.find(v => v.host);
 if (connectedVms) {
 try {
 const response = await fetchWithTenant('/api/vms/cameras');
 const data = await response.json();
 if (response.ok && data.cameras) {
 setCameras(data.cameras);
 }
 } catch (error) {
 console.error('Failed to auto-load cameras:', error);
 }
 }
 };
 loadAllCameras();
 }, [vmsSystems]);

  const loadConfig = async () => {
    try {
      // Load VMS integrations from new adapter system
      const vmsResponse = await fetchWithTenant('/api/vms/integrations');
      if (vmsResponse.ok) {
        const vmsData = await vmsResponse.json();
        const integrations = vmsData.integrations || [];
        
        // Convert backend integrations to UI format
        const vmsList = integrations.map((integration, index) => ({
          id: `vms-${integration.id}`,
          integrationId: integration.id,
          platform: integration.type === 'WAVE_VMS' ? 'wave' : 
                    integration.type === 'MIRASYS_VMS' ? 'mirasys' : 
                    integration.type.toLowerCase().replace('_vms', ''),
          name: integration.name || `VMS ${index + 1}`,
          host: integration.config?.host || '',
          port: integration.config?.port || 7001,
          username: integration.config?.username || '',
          password: integration.config?.password || '',
          useHttps: integration.config?.useHttps !== false
        }));
        
        setVmsSystems(vmsList);
        
        // Also set waveConfig for backward compatibility
        const waveIntegration = integrations.find(i => i.type === 'WAVE_VMS');
        if (waveIntegration) {
          setWaveConfig(waveIntegration.config);
        }
      }
      
      // Load legacy config for Feenics and Intercoms
      const response = await fetchWithTenant('/api/tenant/config');
      const config = await response.json();
      setConfigSource(config._source);
      
      // Load Feenics config - convert single config to array format
      if (config.feenicsConfig && config.feenicsConfig.instance) {
        setAccessControlSystems([{
          id: 'ac-1',
          platform: 'feenics',
          name: 'Primary Access Control',
          host: config.feenicsConfig.host || 'https://api.us.acresecurity.cloud',
          instance: config.feenicsConfig.instance || '',
          username: config.feenicsConfig.username || '',
          password: config.feenicsConfig.password || '',
          connected: false
        }]);
      } else {
        setAccessControlSystems([]);
      }
      
      if (config.intercoms) {
        setIntercoms(config.intercoms);
      } else {
        setIntercoms([]);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

 const loadCompanyInfo = async () => {
 try {
 const response = await fetchWithTenant('/api/tenant/company');
 if (response.ok) {
 const data = await response.json();
 if (data.company) {
 setCompanyInfo(prev => ({
 ...prev,
 ...data.company,
 address: { ...prev.address, ...(data.company.address || {}) },
 contact: { ...prev.contact, ...(data.company.contact || {}) }
 }));
 }
 }
 } catch (error) {
 console.log('Company info not available yet');
 }
 };

 const toggleSection = (section) => {
 setExpandedSections(prev => ({
 ...prev,
 [section]: !prev[section]
 }));
 };

 // VMS System functions
 const addVmsSystem = () => {
 const newVms = {
 id: `vms-${Date.now()}`,
 platform: 'wave',
 name: '',
 host: '',
 port: 7001,
 username: 'admin',
 password: '',
 useHttps: true
 };
 setVmsSystems([...vmsSystems, newVms]);
 setExpandedSections(prev => ({ ...prev, vms: true }));
 };

  const updateVmsSystem = (id, field, value) => {
    setVmsSystems(vmsSystems.map(v => {
      if (v.id !== id) return v;
      
      // If platform is changing, apply platform-specific defaults
      if (field === 'platform' && platformDefaults[value]) {
        const defaults = platformDefaults[value];
        return { 
          ...v, 
          platform: value,
          port: defaults.port,
          useHttps: defaults.useHttps
        };
      }
      
      return { ...v, [field]: value };
    }));
  };

 const removeVmsSystem = (id) => {
 setVmsSystems(vmsSystems.filter(v => v.id !== id));
 };

  const testVmsConnection = async (vms) => {
    setTestingVms(prev => ({ ...prev, [vms.id]: true }));
    try {
      const vmsType = platformToType[vms.platform] || 'WAVE_VMS';
      
      // Test connection using VMS adapter endpoint
      const response = await fetchWithTenant('/api/vms/integrations/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: vmsType,
          integrationId: vms.integrationId,
          config: {
            host: vms.host,
            port: vms.port,
            username: vms.username,
            password: vms.password,
            useHttps: vms.useHttps
          }
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const serverInfo = data.serverInfo || {};
        toast.success(`${vms.name || 'VMS'}: Connected! ${serverInfo.name || ''} v${serverInfo.version || 'unknown'}`);
      } else {
        throw new Error(data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error(`${vms.name || 'VMS'}: Connection failed - ${error.message}`);
    } finally {
      setTestingVms(prev => ({ ...prev, [vms.id]: false }));
    }
  };

 // Access Control System functions
 const addAccessControlSystem = () => {
 const newAc = {
 id: `ac-${Date.now()}`,
 platform: 'feenics',
 name: '',
 host: 'https://api.us.acresecurity.cloud',
 instance: '',
 username: '',
 password: '',
 connected: false
 };
 setAccessControlSystems([...accessControlSystems, newAc]);
 setExpandedSections(prev => ({ ...prev, accessControl: true }));
 };

 const updateAccessControlSystem = (id, field, value) => {
 setAccessControlSystems(accessControlSystems.map(ac =>
 ac.id === id ? { ...ac, [field]: value } : ac
 ));
 };

 const removeAccessControlSystem = (id) => {
 setAccessControlSystems(accessControlSystems.filter(ac => ac.id !== id));
 };

 const testAccessControlConnection = async (ac) => {
 setTestingAccessControl(prev => ({ ...prev, [ac.id]: true }));
 try {
 await fetchWithTenant('/api/tenant/config/feenics', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 host: ac.host,
 instance: ac.instance,
 username: ac.username,
 password: ac.password
 })
 });
 
 const response = await fetchWithTenant('/api/feenics/test');
 const result = await response.json();
 
 if (result.success) {
 setAccessControlSystems(accessControlSystems.map(a =>
 a.id === ac.id ? { ...a, connected: true } : a
 ));
 toast.success(`${ac.name || 'Access Control'}: Connected successfully!`);
 } else {
 setAccessControlSystems(accessControlSystems.map(a =>
 a.id === ac.id ? { ...a, connected: false } : a
 ));
 toast.error(`${ac.name || 'Access Control'}: ${result.message || 'Connection failed'}`);
 }
 } catch (error) {
 toast.error(`${ac.name || 'Access Control'}: Connection failed - ${error.message}`);
 } finally {
 setTestingAccessControl(prev => ({ ...prev, [ac.id]: false }));
 }
 };

 // Intercom functions
 const addIntercom = () => {
 const newIntercom = {
 id: Date.now().toString(),
 name: '',
 cameraId: '',
 n8nWebhookUrl: ''
 };
 setIntercoms([...intercoms, newIntercom]);
 setExpandedSections(prev => ({ ...prev, intercoms: true }));
 };

 const updateIntercom = (id, field, value) => {
 setIntercoms(intercoms.map(i =>
 i.id === id ? { ...i, [field]: value } : i
 ));
 };

 const removeIntercom = (id) => {
 setIntercoms(intercoms.filter(i => i.id !== id));
 };

 const testIntercomWebhook = async (intercom) => {
 if (!intercom.n8nWebhookUrl) {
 toast.error('No webhook URL configured');
 return;
 }

 try {
 const response = await fetch('/api/n8n/test', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ webhookUrl: intercom.n8nWebhookUrl })
 });

 const result = await response.json();

 if (result.success) {
 toast.success(`${intercom.name || 'Intercom'}: Webhook test successful`);
 } else {
 toast.error(`${intercom.name || 'Intercom'}: ${result.message}`);
 }
 } catch (error) {
 toast.error(`${intercom.name || 'Intercom'}: Webhook test failed`);
 }
 };

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Save all VMS systems using adapter endpoints
      for (const vms of vmsSystems) {
        const vmsType = platformToType[vms.platform] || 'WAVE_VMS';
        
        // Check if this is an existing integration (has db id) or new
        if (vms.integrationId) {
          // Update existing integration
          await fetchWithTenant(`/api/vms/integrations/${vms.integrationId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: vms.name || `${vms.platform} VMS`,
              config: {
                host: vms.host,
                port: vms.port,
                username: vms.username,
                password: vms.password,
                useHttps: vms.useHttps
              }
            })
          });
        } else {
          // Create new integration
          const response = await fetchWithTenant('/api/vms/integrations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: vmsType,
              name: vms.name || `${vms.platform} VMS`,
              config: {
                host: vms.host,
                port: vms.port,
                username: vms.username,
                password: vms.password,
                useHttps: vms.useHttps
              }
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            // Update local state with the integration ID
            setVmsSystems(prev => prev.map(v => 
              v.id === vms.id ? { ...v, integrationId: data.integration?.id } : v
            ));
          }
        }
      }
      
      // Save first Access Control config (primary) - keep existing logic
      if (accessControlSystems.length > 0) {
        const primaryAc = accessControlSystems[0];
        await fetchWithTenant('/api/tenant/config/feenics', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            host: primaryAc.host,
            instance: primaryAc.instance,
            username: primaryAc.username,
            password: primaryAc.password
          })
        });
      }
      
      // Save Intercoms
      await fetchWithTenant('/api/tenant/config/intercoms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intercoms })
      });
      
      toast.success('Configuration saved' + (currentTenant ? ` for ${currentTenant.name}` : ''));
    } catch (error) {
      toast.error('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

 const saveCompanyInfo = async () => {
 setSavingCompany(true);
 try {
 const response = await fetchWithTenant('/api/tenant/company', {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ company: companyInfo })
 });

 if (response.ok) {
 toast.success('Company information saved!');
 } else {
 const error = await response.json();
 toast.error('Save failed: ' + (error.message || 'Unknown error'));
 }
 } catch (error) {
 toast.error('Save failed: ' + error.message);
 } finally {
 setSavingCompany(false);
 }
 };

 // Platform options
 const vmsOptions = [
 { value: 'wave', label: 'Hanwha Wave' },
 { value: 'mirasys', label: 'Mirasys' },
 { value: 'milestone', label: 'Milestone XProtect', disabled: true },
 { value: 'genetec', label: 'Genetec', disabled: true }
 ];

 const accessControlOptions = [
 { value: 'feenics', label: 'Feenics (Acre)' },
 { value: 'openpath', label: 'OpenPath', disabled: true },
 { value: 'brivo', label: 'Brivo', disabled: true },
 { value: 'lenel', label: 'LenelS2', disabled: true }
 ];

 // Tab definitions
 const tabs = [
 { id: 'integrations', label: 'Integrations', icon: Link2 },
 { id: 'users', label: 'Users', icon: Users },
 { id: 'organization', label: 'Organization', icon: Building2 },
 { id: 'splunk', label: 'Splunk SIEM', icon: BarChart3 },
 { id: 'audit', label: 'Audit Log', icon: Shield },
 ];

 // Collapsible section header component
 const SectionHeader = ({ section, icon: Icon, title, count, onAdd, addLabel }) => (
    <div 
      className={`collapsible-header ${expandedSections[section] ? 'expanded' : ''}`}
    >
 <div 
 className="collapsible-header-content"
 onClick={() => toggleSection(section)}
 >
 {expandedSections[section] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
 <Icon size={20} />
 <span className="collapsible-header-title">{title}</span>
 {count > 0 && (
 <span className="collapsible-header-count">
 {count} configured
 </span>
 )}
 </div>
 {onAdd && (
 <button 
 className="btn btn-primary btn-sm ml-12" 
 onClick={(e) => { e.stopPropagation(); onAdd(); }}
 >
 <Plus size={14} />
 {addLabel}
 </button>
 )}
 </div>
 );

 // Simple collapsible header (no add button)
 const SimpleSectionHeader = ({ section, icon: Icon, title }) => (
 <div 
 className={`collapsible-header ${expandedSections[section] ? "expanded" : ""}`}
 onClick={() => toggleSection(section)}
 
 >
 <div className="flex-center gap-12">
 {expandedSections[section] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
 <Icon size={20} />
 <span className="collapsible-header-title">{title}</span>
 </div>
 </div>
 );

 // Dropdown style matching Filter by Tenant
 const dropdownStyle = {
 background: 'var(--bg-surface)',
 border: '1px solid rgba(255,255,255,0.15)',
 borderRadius: '6px',
 padding: '0.5rem 2rem 0.5rem 0.75rem',
 color: '#e5e7eb',
 fontSize: '0.85rem',
 cursor: 'pointer',
 minWidth: '200px'
 };

 return (
    <div className="md-page">
      <CcmLicenseBanner />
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Settings size={22} />
            Admin Settings
          </h1>
          <p className="page-subtitle">Configure system connections and preferences</p>
        </div>
        <div className="page-header-actions">
          {currentTenant && <span className="current-site-badge">{currentTenant.name}</span>}
          <button className="tutorial-help-btn" onClick={() => startTutorial(adminSettingsTutorial.module, adminSettingsTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

 {/* Tab Navigation */}
 <div className="admin-tabs">
 {tabs.map((tab) => (
 <button
 key={tab.id}
 className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
 onClick={() => setActiveTab(tab.id)}
 >
 <tab.icon size={18} />
 <span>{tab.label}</span>
              {tab.monitor && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(16,185,129,0.15)', color: '#10b981', marginLeft: 4 }}>Monitor</span>}
 </button>
 ))}
 </div>

 {/* Tab Content */}
 <div className="admin-tab-content">
 
 {/* ==================== INTEGRATIONS TAB ==================== */}
                {activeTab === "integrations" && (
                  <div className="tab-panel">
                    <ControllersManager />
                    <IntegrationsManager />
                  </div>
                )}
 {activeTab === 'users' && (
 <div className="tab-panel">
 
 {/* Pending Registrations Section */}
 <div className="collapsible-section">
 <SimpleSectionHeader
 section="pendingRegistrations"
 icon={UserPlus}
 title="Pending Registrations"
 />
 
 {expandedSections.pendingRegistrations && (
 <div className="collapsible-body">
 <PendingRegistrations hideHeader={true} />
 </div>
 )}
 </div>

 {/* User Management Section */}
 <div className="collapsible-section">
 <SimpleSectionHeader
 section="userManagement"
 icon={Users}
 title="User Management"
 />
 
 {expandedSections.userManagement && (
 <div className="collapsible-body">
 <UserManagement hideHeader={true} />
 </div>
 )}
 </div>

 </div>
 )}

 {/* ==================== ORGANIZATION TAB ==================== */}
 {activeTab === 'organization' && (
 <div className="tab-panel">
 
 {/* Company Information */}
 <div className="card">
 <h3 className="card-title">
 <Building2 size={20} />
 Company Information
 </h3>
 
 <div className="form-group">
 <label className="form-label">Company Name</label>
 <input
 type="text"
 className="form-input company-name-input"
 placeholder="Your Company Name"
 value={companyInfo.name}
 onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
 />
 </div>

 <h4 className="section-subtitle">
 <MapPin size={16} />
 Address
 </h4>
 
 <div className="form-grid">
 <div className="form-group form-grid-full">
 <label className="form-label">Street Address</label>
 <input
 type="text"
 className="form-input"
 placeholder="123 Main Street"
 value={companyInfo.address.street}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 address: { ...companyInfo.address, street: e.target.value }
 })}
 />
 </div>
 <div className="form-group">
 <label className="form-label">City</label>
 <input
 type="text"
 className="form-input"
 placeholder="New York"
 value={companyInfo.address.city}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 address: { ...companyInfo.address, city: e.target.value }
 })}
 />
 </div>
 <div className="form-group">
 <label className="form-label">State/Province</label>
 <input
 type="text"
 className="form-input"
 placeholder="NY"
 value={companyInfo.address.state}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 address: { ...companyInfo.address, state: e.target.value }
 })}
 />
 </div>
 <div className="form-group">
 <label className="form-label">ZIP/Postal Code</label>
 <input
 type="text"
 className="form-input"
 placeholder="10001"
 value={companyInfo.address.zip}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 address: { ...companyInfo.address, zip: e.target.value }
 })}
 />
 </div>
 <div className="form-group">
 <label className="form-label">Country</label>
 <input
 type="text"
 className="form-input"
 placeholder="United States"
 value={companyInfo.address.country}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 address: { ...companyInfo.address, country: e.target.value }
 })}
 />
 </div>
 </div>

 <h4 className="section-subtitle mt-3">
 <Mail size={16} />
 Contact Information
 </h4>
 
 <div className="form-grid">
 <div className="form-group">
 <label className="form-label">Phone</label>
 <input
 type="tel"
 className="form-input"
 placeholder="(555) 123-4567"
 value={companyInfo.contact.phone}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 contact: { ...companyInfo.contact, phone: e.target.value }
 })}
 onBlur={handlePhoneBlur}
 />
 </div>
 <div className="form-group">
 <label className="form-label">Email</label>
 <input
 type="email"
 className="form-input"
 placeholder="contact@company.com"
 value={companyInfo.contact.email}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 contact: { ...companyInfo.contact, email: e.target.value }
 })}
 />
 </div>
 <div className="form-group">
 <label className="form-label">Website</label>
 <input
 type="url"
 className="form-input"
 placeholder="https://www.company.com"
 value={companyInfo.contact.website}
 onChange={(e) => setCompanyInfo({ 
 ...companyInfo, 
 contact: { ...companyInfo.contact, website: e.target.value }
 })}
 />
 </div>
 <div className="form-group">
 <label className="form-label">Industry/Sector</label>
 <select
 style={dropdownStyle}
 value={companyInfo.industry}
 onChange={(e) => setCompanyInfo({ ...companyInfo, industry: e.target.value })}
 >
 <option value="">Select industry...</option>
 <option value="k12">K-12 Education</option>
 <option value="higher-ed">Higher Education</option>
 <option value="healthcare">Healthcare</option>
 <option value="commercial">Commercial Real Estate</option>
 <option value="residential">Residential Property</option>
 <option value="government">Government</option>
 <option value="retail">Retail</option>
 <option value="manufacturing">Manufacturing</option>
 <option value="other">Other</option>
 </select>
 </div>
 </div>

 <div className="form-actions mt-3">
 <button
 className="btn btn-primary"
 onClick={saveCompanyInfo}
 disabled={savingCompany}
 >
 {savingCompany ? (
 <><RefreshCw size={16} className="spin" /> Saving...</>
 ) : (
 <><Save size={16} /> Save Company Info</>
 )}
 </button>
 </div>
 </div>

 {/* CCM Licensing */}
 <CcmLicenseCard />

 {/* Org-level umbrella license (Phase 2) */}
 <OrgLicenseCard />

        {/* OAuth / SSO Settings Section */}
        <div className="card mt-4">
          <OAuthSettings />
        </div>
      </div>
    )}

 {activeTab === 'splunk' && (
 <div className="tab-panel">
   <SplunkSettings />
 </div>
 )}

 {activeTab === 'audit' && (
 <div className="tab-panel">
   <AuditLogTab />
 </div>
 )}

      </div>

      {showPrompt && (
        <TutorialPrompt
          module={adminSettingsTutorial.module}
          moduleName={adminSettingsTutorial.moduleName}
          steps={adminSettingsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default AdminSettings;
